import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import axios from 'axios';
import useFetchCart from './FetchCart';
import { useCart } from './CartContext';
import './ReviewOrder.css';
import BASE_URL, { getImageURL } from '../../config';

const ReviewOrderDe = () => {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const stripe = useStripe();
  const elements = useElements();
  const { cart } = useFetchCart();
  const { fetchCart } = useCart();
  const navigate = useNavigate();

  const [tAmount, settAmount] = useState(0);
  const [shipaddress, setShipaddress] = useState(null);
  const [billaddress, setBilladdress] = useState(null);
  const [deltime, setDeltime] = useState(null);
  const [deldate, setDeldate] = useState(null);
  const paymentmethod = "stripe";
  const [deliveryFee, setDeliveryFee] = useState(0);

  const stripeElementStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
        padding: '10px 15px',
      },
    },
  };

  useEffect(() => {
    const storedOrderData = JSON.parse(localStorage.getItem('orderData'));
    if (storedOrderData) {
      setOrderData(storedOrderData);
      setShipaddress(storedOrderData.shipping_address);
      setBilladdress(storedOrderData.shipping_address);
      const formattedDate = new Date(storedOrderData.delivery_date).toISOString().split('T')[0];
      setDeldate(formattedDate);
      setDeltime(storedOrderData.delivery_time);
      settAmount(storedOrderData.total);
      setDeliveryFee(storedOrderData.delivery_fee ?? parseFloat(localStorage.getItem('deliveryFee') || 0));
    } else {
      navigate('/checkout');
    }
  }, [navigate]);
  
  const handlePayment = async () => {
    try {
      setLoading(true);
      setStatusMessage(null);

      const paymentResponse = await axios.post(`${BASE_URL}/api/payment/`, {
        amount: orderData.total
      });

      const { client_secret } = paymentResponse.data;

      if (!stripe || !elements) {
        setLoading(false);
        setStatusMessage({ type: 'error', text: 'Stripe wurde nicht initialisiert. Bitte laden Sie die Seite neu.' });
        return;
      }

      const cardNumber = elements.getElement(CardNumberElement);
      const cardExpiry = elements.getElement(CardExpiryElement);
      const cardCvc = elements.getElement(CardCvcElement);

      if (!cardNumber || !cardExpiry || !cardCvc) {
        setLoading(false);
        setStatusMessage({ type: 'error', text: 'Kartenelemente nicht gefunden. Bitte laden Sie die Seite neu.' });
        return;
      }

      const paymentResult = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardNumber
        }
      });

      if (paymentResult.error) {
        setStatusMessage({ type: 'error', text: paymentResult.error.message });
        setLoading(false);
      } else if (paymentResult.paymentIntent.status === 'succeeded') {
        await finalizeCheckout();
      }
    } catch (error) {
      console.error('Zahlungsfehler:', error);
      setStatusMessage({ type: 'error', text: 'Zahlung fehlgeschlagen. Bitte versuchen Sie es erneut.' });
      setLoading(false);
    }
  };

  const finalizeCheckout = async () => {
    const checkoutData = {
      shipping_address: shipaddress,
      delivery_date: deldate,
      delivery_time: deltime,
      payment_method: paymentmethod
    };

    const authToken = localStorage.getItem('authToken');

    try {
      const checkoutResponse = await axios.post(`${BASE_URL}/api/checkout/`, checkoutData, {
        headers: {
          'Authorization': `Token ${authToken}`
        }
      });
      
      const order_id = checkoutResponse.data.id;
      localStorage.removeItem('orderData');
      fetchCart(); // Clear the cart badge in the navbar
      navigate(`/ordersummary/${order_id}`);
    } catch (error) {
      console.error('Bestellfehler:', error?.response?.data || error);
      const serverError = error?.response?.data?.error || 'Unbekannter Fehler';
      setStatusMessage({ type: 'error', text: `Zahlung war erfolgreich, aber die Bestellung konnte nicht erstellt werden: ${serverError}. Bitte kontaktieren Sie den Support.` });
    } finally {
      setLoading(false);
    }
  };

  const getItemPrice = (item) => {
    const price = parseFloat(item.discount_price) || parseFloat(item.price) || 0;
    return price;
  };

  if (!orderData || !cart) return <div className="review-loading">Wird geladen...</div>;

  return (
    <div className="review-page">
      <h1>Bestellung überprüfen</h1>

      {statusMessage && (
        <div className={`review-status ${statusMessage.type}`}>
          {statusMessage.type === 'success' ? '✅ ' : '⚠️ '}
          {statusMessage.text}
        </div>
      )}

      <div className="review-grid">
        {/* ── Left column: Order details ── */}
        <div>
          {/* Delivery information */}
          <div className="review-section">
            <h2 className="review-section-title">Lieferinformationen</h2>
            <div className="review-info-card">
              <div className="review-info-row">
                <span className="review-info-label">Rechnungsadresse</span>
                <span className="review-info-value">
                  {orderData.billing_address?.address_line1}
                  {orderData.billing_address?.address_line2 ? `, ${orderData.billing_address.address_line2}` : ''}
                  <br />
                  {orderData.billing_address?.postal_code} {orderData.billing_address?.city}
                  <br />
                  {orderData.billing_address?.country}
                </span>
              </div>
              <div className="review-info-row">
                <span className="review-info-label">Lieferadresse</span>
                <span className="review-info-value">
                  {orderData.shipping_address?.address_line1}
                  {orderData.shipping_address?.address_line2 ? `, ${orderData.shipping_address.address_line2}` : ''}
                  <br />
                  {orderData.shipping_address?.postal_code} {orderData.shipping_address?.city}
                  <br />
                  {orderData.shipping_address?.country}
                </span>
              </div>
              <div className="review-info-row">
                <span className="review-info-label">Lieferart</span>
                <span className="review-info-value">{orderData.delivery_type}</span>
              </div>
              <div className="review-info-row">
                <span className="review-info-label">Lieferdatum</span>
                <span className="review-info-value">{deldate}</span>
              </div>
              <div className="review-info-row">
                <span className="review-info-label">Lieferzeit</span>
                <span className="review-info-value">{orderData.delivery_time}</span>
              </div>
            </div>
          </div>

          {/* Cart items */}
          <div className="review-section">
            <h2 className="review-section-title">Ihr Warenkorb</h2>
            {cart.items.map(item => (
              <div className="review-item" key={item.id}>
                <img
                  className="review-item-image"
                  src={item.item.mainimage ? getImageURL(item.item.mainimage) : 'placeholder.jpg'}
                  alt={item.item.title}
                />
                <div className="review-item-info">
                  <p className="review-item-title">{item.item.title}</p>
                  <span className="review-item-qty">Menge: {item.quantity}</span>
                </div>
                <span className="review-item-price">
                  {(getItemPrice(item.item) * item.quantity).toFixed(2)} CHF
                </span>
              </div>
            ))}

            <div className="review-totals">
              <div className="review-total-row">
                <span>Zwischensumme</span>
                <span>{(parseFloat(tAmount || 0) - parseFloat(deliveryFee || 0)).toFixed(2)} CHF</span>
              </div>
              <div className="review-total-row">
                <span>Liefergebühr</span>
                <span>{parseFloat(deliveryFee || 0) === 0
                  ? <span style={{color:'#2e7d32', fontWeight:600}}>Kostenlos</span>
                  : `${parseFloat(deliveryFee || 0).toFixed(2)} CHF`}
                </span>
              </div>
              <div className="review-total-row tax">
                <span>Total exkl. MwSt.</span>
                <span>{(parseFloat(tAmount || 0) / 1.081).toFixed(2)} CHF</span>
              </div>
              <div className="review-total-row tax">
                <span>MwSt. (8.1%)</span>
                <span>{(parseFloat(tAmount || 0) - parseFloat(tAmount || 0) / 1.081).toFixed(2)} CHF</span>
              </div>
              <div className="review-total-row grand">
                <span>Gesamt</span>
                <span>{parseFloat(tAmount || 0).toFixed(2)} CHF</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right column: Payment ── */}
        <div>
          <div className="review-payment">
            <h2 className="review-payment-title">Zahlung</h2>

            <div className="review-method-tab">
              <span className="review-method-icon">💳</span>
              <span>Karte</span>
            </div>

            <div className="review-field">
              <label>Kartennummer</label>
              <div className="review-field-input">
                <CardNumberElement options={stripeElementStyle} />
              </div>
            </div>

            <div className="review-card-row">
              <div className="review-field">
                <label>Ablaufdatum</label>
                <div className="review-field-input">
                  <CardExpiryElement options={stripeElementStyle} />
                </div>
              </div>
              <div className="review-field">
                <label>Sicherheitscode</label>
                <div className="review-field-input">
                  <CardCvcElement options={stripeElementStyle} />
                </div>
              </div>
            </div>

            <p className="review-legal-notice">
              Mit dieser Bestellung akzeptierst du unsere{' '}
              <a href="/legal" target="_blank" rel="noreferrer">Allgemeine Geschäftsbedingungen</a>{' '}
              und nimmst unsere{' '}
              <a href="/privacy-policy" target="_blank" rel="noreferrer">Datenschutzerklärung</a>{' '}
              zur Kenntnis.
            </p>

            <button
              onClick={handlePayment}
              className="review-pay-btn"
              disabled={loading}
            >
              {loading ? 'Wird verarbeitet...' : `${parseFloat(tAmount || 0).toFixed(2)} CHF bezahlen`}
            </button>

            <a href="/checkout" className="review-back-link">← Zurück zur Kasse</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewOrderDe;